import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { BaryModalEvent, IBaryModalEvent } from '../../../../models/bary-modal-event';
import { forkJoin } from 'rxjs';
import { mapAsBody } from '../../../shared/util/request-util';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OfferHandlingTypeExtendedService } from '../../../../services/offer-handling-type-extended.service';
import { RefDimensionExtendedService } from '../../../../services/ref-dimension-extended.service';
import { RefDimension } from '../../../../models/ref-dimension.model';
import { HandlingTypeViewModel } from '../../../../models/handling-type-view.model';
import { MDBBootstrapModulesPro, MDBModalRef } from 'ng-uikit-pro-standard';
import { TransportFormGroup } from '../../../../models/forms/TransportFormGroup';
import { HandlingTypeEnum } from '../../../../models/enums/handling-type.enum';
import { CurrentDataService } from '../../../../services/current-data-service';
import { MeasureUnitEnum } from '../../../../models/vehicle.model';
import { BaryComponentHelper } from '../../../shared/util/bary-component-helper';
import { TransportItem } from '../../../../models/transport-item.model';
import { take } from 'rxjs/operators';
import { PricingTransporterConfigurationService } from '../../../../services/pricing-transporter-configuration.service';
import { IPricingTransporterConfiguration } from '../../../../models/pricing-transporter.configuration';
import { BaryValidator } from '../../../../validators/bary.validator';
import { PalletMinLDM } from '../../../../validators/pallet-min-ldm.validator';
import { HandlingType, LIST_HANDLING_TYPE } from '../../../../models/handling-type.model';
import { ObjLabelValue } from '../../../../models/obj-label-value.interface';
import { TRANSPORT_ITEM_ESTIMATE_STEP } from '../../../../models/enums/transport-item-estimate-step.enum';
import { BaryOfferFormComponent } from '../../../shared/ui/forms/bary-offer-form/bary-offer-form.component';
import { TransportEstimationStepComponent } from './settings-step/transport-estimation-step.component';
import { BaryButtonComponent } from '../../../shared/components';
import { DecimalPipe, NgIf } from '@angular/common';
import { TransportExtraConditionFormComponent } from '../transport-extra-condition-form/transport-extra-condition-form.component';
import { ServiceTypeEnum } from '../../../../models/enums/offer-service-type.enum';

@Component({
  selector: 'jhi-modal-estimate',
  standalone: true,
  imports: [
    MDBBootstrapModulesPro,
    BaryOfferFormComponent,
    TransportEstimationStepComponent,
    BaryButtonComponent,
    TranslateModule,
    DecimalPipe,
    TransportExtraConditionFormComponent,
    NgIf,
  ],
  templateUrl: './modal-estimate.component.html',
  styleUrls: ['./modal-estimate.component.scss'],
})
export class ModalEstimateComponent implements OnInit {
  readonly #modalRef = inject(MDBModalRef);
  readonly #translateService = inject(TranslateService);
  readonly #currentDataService = inject(CurrentDataService);
  readonly #offerHandlingTypeService = inject(OfferHandlingTypeExtendedService);
  readonly #refDimensionService = inject(RefDimensionExtendedService);
  readonly #pricingTransporterConfigurationService = inject(PricingTransporterConfigurationService);

  public action = BaryComponentHelper.generateAction<IBaryModalEvent>();

  public handlingType = HandlingTypeEnum;
  public refDimensions: RefDimension[] = [];
  public dimensionOpts: { label: string; value: number }[] = [];

  public transportFormGroup!: TransportFormGroup;
  public pricingTransporterConfiguration?: IPricingTransporterConfiguration;

  public currentStep: TRANSPORT_ITEM_ESTIMATE_STEP = TRANSPORT_ITEM_ESTIMATE_STEP.SETTING;

  protected readonly TRANSPORT_STEP = TRANSPORT_ITEM_ESTIMATE_STEP;
  protected readonly FormGroup = FormGroup;

  public handlingTypes: HandlingTypeViewModel[] = [];
  public handlingTypesOpt: ObjLabelValue[] = [];

  public transportItemForm!: FormGroup;
  public currentTransportItem!: TransportItem;

  public CRATE_OF_WINE: HandlingTypeEnum = HandlingTypeEnum.CRATE_OF_WINE;

  public initForm(transportItem: TransportItem): void {
    const handlingType = new FormControl({ value: transportItem?.handlingType || HandlingTypeEnum.PALLET, disabled: false }, [Validators.required]);
    this.transportItemForm = new FormGroup(
      {
        handlingType,
        dimension: new FormControl({ value: transportItem?.dimension, disabled: false }, this.hasDimension(transportItem.handlingType) ? [Validators.required] : []),
        length: new FormControl({ value: transportItem?.length, disabled: this.isCrateOfWine(transportItem.handlingType) }, this.getValidators('length', handlingType)),
        width: new FormControl({ value: transportItem?.width, disabled: this.isCrateOfWine(transportItem.handlingType) }, this.getValidators('width', handlingType)),
        maxHeight: new FormControl({ value: transportItem?.maxHeight, disabled: this.isCrateOfWine(transportItem.handlingType) }, this.getValidators('maxHeight', handlingType)),
        weight: new FormControl({ value: transportItem?.weight, disabled: this.isCrateOfWine(transportItem.handlingType) }, this.getValidators('weight', handlingType)),
        quantity: new FormControl({ value: transportItem?.quantity, disabled: false }, [
          Validators.required,
          Validators.min(1),
          BaryValidator.maxForPalletAndParcel(
            handlingType,
            this.pricingTransporterConfiguration?.quotationItemsPalletMaxQuantity,
            this.pricingTransporterConfiguration?.quotationItemsParcelMaxQuantity
          ),
        ]),
        groundPalletQuantity: new FormControl({ value: transportItem?.groundPalletQuantity, disabled: false }),
        otherSpecificQuantity: new FormControl({ value: transportItem?.otherSpecificQuantity, disabled: false }),
        description: new FormControl({ value: transportItem?.description, disabled: false }),
        vehicleCategory: new FormControl(
          { value: transportItem?.vehicleCategory?.code, disabled: false },
          this.transportFormGroup.controls.serviceType.value === ServiceTypeEnum.FTL ? [Validators.required] : []
        ),
        vehicleType: new FormControl(
          { value: transportItem?.vehicleType?.code, disabled: false },
          this.transportFormGroup.controls.serviceType.value === ServiceTypeEnum.FTL ? [Validators.required] : []
        ),
        lengthUnit: new FormControl({ value: transportItem?.lengthUnit?.value || MeasureUnitEnum.CENTIMETER, disabled: true }),
        weightUnit: new FormControl({ value: transportItem?.weightUnit?.value || MeasureUnitEnum.KG, disabled: true }),
        other: new FormControl({ value: transportItem?.other, disabled: false }),
      },
      { validators: [PalletMinLDM] }
    );
  }

  public updateDimensionOpts(): void {
    this.dimensionOpts = (this.refDimensions || [])
      .filter((dimension: RefDimension) => dimension.handlingType === this.transportItemForm.controls['handlingType']?.value && dimension.activated)
      .map((dimension: RefDimension) => {
        return {
          label:
            dimension.handlingType === HandlingTypeEnum.CRATE_OF_WINE
              ? (this.#translateService.instant('LABEL.FORMAT.COLON_AND_VALUE', { format: dimension.label }) as string)
              : `${dimension.label}`,
          value: dimension.id,
        };
      });
    if ([HandlingTypeEnum.PALLET, HandlingTypeEnum.CONTAINER].includes(this.transportItemForm.controls['handlingType']?.value)) {
      this.dimensionOpts.push({ label: this.#translateService.instant('LABEL.OTHER_FORMAT'), value: -1 });
    }
  }

  getValidators(formControlName: string, handlingType: FormControl): ValidatorFn[] {
    switch (formControlName) {
      case 'length':
        if (this.isCrateOfWine()) return [];
        return [
          Validators.required,
          Validators.min(1),
          BaryValidator.maxForPalletAndParcel(
            handlingType,
            this.pricingTransporterConfiguration?.quotationItemsPalletMaxLength,
            this.pricingTransporterConfiguration?.quotationItemsParcelMaxLength
          ),
        ];
      case 'width':
        if (this.isCrateOfWine()) return [];
        return [
          Validators.required,
          Validators.min(1),
          BaryValidator.maxForPalletAndParcel(
            handlingType,
            this.pricingTransporterConfiguration?.quotationItemsPalletMaxWidth,
            this.pricingTransporterConfiguration?.quotationItemsParcelMaxWidth
          ),
        ];
      case 'maxHeight':
        if (this.isCrateOfWine()) return [];
        return [
          Validators.required,
          Validators.min(1),
          BaryValidator.maxForPalletAndParcel(
            handlingType,
            this.pricingTransporterConfiguration?.quotationItemsPalletMaxHeight,
            this.pricingTransporterConfiguration?.quotationItemsParcelMaxHeight
          ),
        ];
      case 'weight':
        if (this.isCrateOfWine()) return [];
        return [
          Validators.required,
          Validators.min(1),
          BaryValidator.maxForPalletAndParcel(
            handlingType,
            this.pricingTransporterConfiguration?.quotationItemsPalletMaxWeight,
            this.pricingTransporterConfiguration?.quotationItemsParcelMaxWeight
          ),
        ];
      default:
        return [];
    }
  }

  public hasDimension(handlingType?: HandlingTypeEnum): boolean {
    return !!handlingType && [HandlingTypeEnum.PALLET, HandlingTypeEnum.CRATE_OF_WINE, HandlingTypeEnum.CONTAINER].includes(handlingType!);
  }

  public isCrateOfWine(handlingType?: HandlingTypeEnum): boolean {
    return !!handlingType && [HandlingTypeEnum.CRATE_OF_WINE].includes(handlingType);
  }

  public ngOnInit(): void {
    forkJoin({
      transporter: this.#currentDataService.getCurrentTransporter(),
      customer: this.#currentDataService.getCustomer(),
      handlingTypes: this.#offerHandlingTypeService.getCurrent(),
      refDimensions: this.#refDimensionService.query(),
      config: this.#pricingTransporterConfigurationService.retrieveConfiguration().pipe(take(1)).pipe(mapAsBody),
    }).subscribe(data => {
      this.handlingTypes = data.handlingTypes;
      this.refDimensions = data.refDimensions;
      this.pricingTransporterConfiguration = data.config;
      this.initForm(this.currentTransportItem);
      this.initHandlingType();
      this.updateFormByHandlingType(this.transportItemForm.controls['handlingType'].value);
      this.updateDimensionOpts();
      if (this.dimensionOpts.length > 0 && this.transportItemForm.controls['width'].value && this.transportItemForm.controls['length'].value) {
        const dimension = this.refDimensions.find(
          dim =>
            dim.width == this.transportItemForm.controls['width'].value &&
            dim.length == this.transportItemForm.controls['length'].value &&
            dim.handlingType == this.transportItemForm.controls['handlingType'].value
        );
        if (dimension) {
          this.transportItemForm.controls['dimension'].setValue(dimension.id);
        }
      }
      this.initListeners();
    });
  }

  public initListeners(): void {
    // Si on modifie le handlingType, on mets à jours la liste des dimensions et le formulaire
    this.transportItemForm.controls['handlingType']?.valueChanges.subscribe((val): void => {
      this.updateFormByHandlingType(val);
      this.updateDimensionOpts();
    });

    // Si on modifie les dimensions, on mets à jours les champs suivant, length, width, maxHeight, other, otherSpecific
    this.transportItemForm.controls['dimension'].valueChanges.subscribe((dimensionId): void => {
      if (!dimensionId && dimensionId !== -1) {
        return;
      } // -1 est l'id de la valeur "Autre format pour les palettes"
      const val: RefDimension = this.refDimensions.find(dim => dim.id === dimensionId)!;
      if (this.transportItemForm.controls['handlingType']?.value !== HandlingTypeEnum.CRATE_OF_WINE && val) {
        this.transportItemForm.controls['length']?.setValue(val.length ? Math.ceil(val.length) : val.length);
        this.transportItemForm.controls['width']?.setValue(val.width ? Math.ceil(val.width) : val.width);
        this.transportItemForm.controls['maxHeight']?.setValue(val.maxHeight ? Math.ceil(val.maxHeight) : val.maxHeight);
        this.transportItemForm.controls['other']?.setValue(val.other ? Math.ceil(val.other) : val.other);
        this.transportItemForm.controls['otherSpecificQuantity'].setValue(0);
      } else {
        this.transportItemForm.controls['otherSpecificQuantity'].setValue((val?.other || 0) * this.transportItemForm.controls['quantity'].value || 0);
      }
    });

    // si l'utilisateur modifie la valeur de la largeur, on passe alors en autre format
    this.transportItemForm.controls['width'].valueChanges.subscribe(newVal => {
      if (
        [HandlingTypeEnum.PALLET, HandlingTypeEnum.CONTAINER].includes(this.transportItemForm.controls['handlingType']?.value) &&
        !this.refDimensions.find(it => it.width === newVal)
      ) {
        this.transportItemForm.controls['dimension'].setValue(-1, { emitEvent: false });
      }
    });
    // si l'utilisateur modifie la valeur de la longueur, on passe alors en autre format
    this.transportItemForm.controls['length'].valueChanges.subscribe(newVal => {
      if (
        [HandlingTypeEnum.PALLET, HandlingTypeEnum.CONTAINER].includes(this.transportItemForm.controls['handlingType']?.value) &&
        !this.refDimensions.find(it => it.width === newVal)
      ) {
        this.transportItemForm.controls['dimension'].setValue(-1, { emitEvent: false });
      }
    });

    // Si on modifie la quantité, on mets à jours les quantités spécifiques
    this.transportItemForm.controls['quantity'].valueChanges.subscribe((val: number): void => {
      if (this.transportItemForm.controls['handlingType']?.value === HandlingTypeEnum.CRATE_OF_WINE) {
        const dimension: RefDimension = this.refDimensions.find(dim => dim.id === this.transportItemForm.controls['dimension']?.value)!;
        this.transportItemForm.controls['otherSpecificQuantity'].setValue(val * (dimension?.other || 0));
      }
    });

    //Si on modifie le type de service en FTL, la categorie et le type de véhicule est obligatoire, sinon on n'utilise les données
    this.transportFormGroup.controls.serviceType.valueChanges.subscribe(val => {
      if (val === ServiceTypeEnum.FTL) {
        this.transportItemForm.controls['vehicleCategory'].addValidators(Validators.required);
        this.transportItemForm.controls['vehicleType'].addValidators(Validators.required);
      } else {
        this.transportItemForm.controls['vehicleCategory'].removeValidators(Validators.required);
        this.transportItemForm.controls['vehicleType'].removeValidators(Validators.required);
        this.transportItemForm.controls['vehicleCategory'].setValue(undefined);
        this.transportItemForm.controls['vehicleType'].setValue(undefined);
      }
    });
  }

  /**
   * Permet de mettre à jour le formulaire en fonction "HandlingType" sélectionné.
   *
   * @param handlingTypeSelected
   * @private
   */
  private updateFormByHandlingType(handlingTypeSelected: HandlingTypeEnum): void {
    switch (handlingTypeSelected) {
      case HandlingTypeEnum.BUNDLE:
      case HandlingTypeEnum.INDIVISIBLE:
      case HandlingTypeEnum.ROLL:
      case HandlingTypeEnum.PARCEL:
      case HandlingTypeEnum.PERSONALIZED:
        this.updateControl(this.transportItemForm.controls['length'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['maxHeight'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['width'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['weight'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['dimension'] as FormControl, true);
        this.updateControl(this.transportItemForm.controls['description'] as FormControl, false);
        break;
      case HandlingTypeEnum.PALLET:
        this.updateControl(this.transportItemForm.controls['length'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['maxHeight'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['width'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['weight'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['dimension'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['description'] as FormControl, false);
        this.transportItemForm.controls['dimension'].setValue(this.currentTransportItem.dimension);
        break;
      case HandlingTypeEnum.CONTAINER:
        this.updateControl(this.transportItemForm.controls['length'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['maxHeight'] as FormControl, false, []);
        this.updateControl(this.transportItemForm.controls['width'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['weight'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['dimension'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['description'] as FormControl, false);
        this.transportItemForm.controls['dimension'].setValue(this.currentTransportItem.dimension);
        break;
      case HandlingTypeEnum.CRATE_OF_WINE:
        this.updateControl(this.transportItemForm.controls['length'] as FormControl, true);
        this.updateControl(this.transportItemForm.controls['maxHeight'] as FormControl, true);
        this.updateControl(this.transportItemForm.controls['width'] as FormControl, true);
        this.updateControl(this.transportItemForm.controls['weight'] as FormControl, true);
        this.updateControl(this.transportItemForm.controls['dimension'] as FormControl, false);
        this.updateControl(this.transportItemForm.controls['description'] as FormControl, false);
        break;
      default:
    }
  }

  /**
   * Permet de mettre à jour un formControl:
   * disabled = true, on retire la valeur + les validateurs et on désactive le champs
   * disabled = false, on ajoute les validateurs et on active le champs
   * @param formControl
   * @param disabled
   * @param validator
   * @private
   */
  private updateControl(formControl: FormControl, disabled: boolean, validator: ValidatorFn[] = [Validators.required]): void {
    if (disabled) {
      formControl.disable();
      formControl.removeValidators(validator);
      formControl.setValue(undefined);
    } else {
      formControl.enable();
      formControl.addValidators(validator);
    }
    formControl.updateValueAndValidity();
  }

  public get weightTotal(): number | null {
    if (this.transportItemForm?.controls['weight']?.value && this.transportItemForm?.controls['quantity']?.value) {
      return this.transportItemForm.controls['weight'].value * this.transportItemForm.controls['quantity'].value;
    }
    return null;
  }

  public get surfaceTotal(): number | null {
    if (this.transportItemForm?.controls['length']?.value && this.transportItemForm?.controls['width']?.value && this.transportItemForm?.controls['quantity']?.value) {
      return (
        (((this.transportItemForm.controls['length'].value! / 100) * this.transportItemForm.controls['width'].value) / 100) * this.transportItemForm.controls['quantity'].value
      );
    }
    return null;
  }

  public get volumeTotal(): number | null {
    if (this.surfaceTotal && this.transportItemForm?.controls['maxHeight']?.value) {
      return (this.surfaceTotal * this.transportItemForm.controls['maxHeight'].value) / 100;
    }
    return null;
  }

  public get ldmTotal(): number | null {
    if (this.surfaceTotal) return this.surfaceTotal / 2.4;
    return null;
  }

  /**
   * Permet d'aller à l'étape suivante.
   */
  public nextStep(): void {
    if (
      this.currentStep === TRANSPORT_ITEM_ESTIMATE_STEP.SETTING &&
      (this.transportFormGroup.controls.isControlledTemperature.value || this.transportFormGroup.controls.isHazardousMaterial.value)
    ) {
      this.currentStep = TRANSPORT_ITEM_ESTIMATE_STEP.SPECIAL;
    } else {
      this.initHandlingType();
      this.currentStep = TRANSPORT_ITEM_ESTIMATE_STEP.ITEM;
    }
  }

  /**
   * Permet d'aller à l'étape précedente.
   */
  public returnStep(): void {
    if (
      this.currentStep === TRANSPORT_ITEM_ESTIMATE_STEP.ITEM &&
      (this.transportFormGroup.controls.isControlledTemperature.value || this.transportFormGroup.controls.isHazardousMaterial.value)
    ) {
      this.transportFormGroup.controls.items = [];
      this.currentStep = TRANSPORT_ITEM_ESTIMATE_STEP.SPECIAL;
    } else if (
      (this.currentStep === TRANSPORT_ITEM_ESTIMATE_STEP.ITEM &&
        !this.transportFormGroup.controls.isControlledTemperature.value &&
        !this.transportFormGroup.controls.isHazardousMaterial.value) ||
      this.currentStep === TRANSPORT_ITEM_ESTIMATE_STEP.SPECIAL
    ) {
      this.currentStep = TRANSPORT_ITEM_ESTIMATE_STEP.SETTING;
    }
  }

  /**
   * Ferme la modale si from.close = true,
   * sinon et si le formulaire est valide, on ferme la modale et on envoie le formulaire afin de l'ajouter dans la transportForm.
   *
   * @param from
   */
  public handleClick(from: any): void {
    if (from.close) {
      // If closing modal and no items, we reset at least special condition
      if (this.transportFormGroup.controls.items.length === 0) {
        this.transportFormGroup.controls.isControlledTemperature.setValue(false);
        this.transportFormGroup.controls.isHazardousMaterial.setValue(false);
      }

      this.action.next(new BaryModalEvent(from));
      this.#modalRef.hide();
      return;
    }
    if (this.transportItemForm?.invalid) {
      this.transportItemForm?.markAllAsTouched();
      return;
    }

    this.setValueIfCrateOfWine();

    const result = this.transportItemForm;
    //On saisie le poids par unité mais on enregistre le poids total
    result.controls['weight'].setValue(result.controls['weight'].value * result.controls['quantity'].value);
    this.action.next(new BaryModalEvent({ ...from, value: this.transportItemForm, submit: true }));
    this.#modalRef.hide();
  }

  /**
   * Si le handlingType == CRATE_OF_WINE,
   * on remet les valeurs de la dimension sélectionnée dans les champs suivant, wifth, length, maxHeight, weight et other.
   *
   */
  public setValueIfCrateOfWine(): void {
    if (this.transportItemForm?.controls?.['handlingType'].value === HandlingTypeEnum.CRATE_OF_WINE) {
      const val: RefDimension = this.refDimensions.find(dim => dim.id === this.transportItemForm.controls['dimension']?.value)!;
      this.transportItemForm.controls['length']?.setValue(val.length ? Math.ceil(val.length) : val.length);
      this.transportItemForm.controls['width']?.setValue(val.width ? Math.ceil(val.width) : val.width);
      this.transportItemForm.controls['maxHeight']?.setValue(val.maxHeight ? Math.ceil(val.maxHeight) : val.maxHeight);
      this.transportItemForm.controls['weight']?.setValue(val.weight ? Math.ceil(val.weight) : val.weight);
      this.transportItemForm.controls['other']?.setValue(val.other ? Math.ceil(val.other) : val.other);
    }
  }

  protected readonly HandlingType = HandlingType;

  private initHandlingType(): void {
    this.handlingTypesOpt = (this.handlingTypes || [])
      .filter(handlingType => {
        if (this.transportFormGroup.controls.isControlledTemperature.value) {
          return handlingType.controlledTemperature && !handlingType.hazardousMaterial;
        }
        if (this.transportFormGroup.controls.isHazardousMaterial.value) {
          return handlingType.hazardousMaterial && !handlingType.controlledTemperature;
        }
        return !handlingType.controlledTemperature && !handlingType.hazardousMaterial;
      })
      .flatMap(v => v.handlingTypes)
      .map(v => LIST_HANDLING_TYPE[v])
      .map((handlingType: HandlingType) => ({
        label: this.#translateService.instant(handlingType.labelKey),
        value: handlingType.code,
      }));
    /** Par défault le handlingType est initialisé à "COLIS", si le transporter ne fait pas de "COLIS", on change la valeur avec le 1er handlingType du transporter **/
    if (!this.handlingTypesOpt.map(handlingType => handlingType.value).includes(this.transportItemForm?.controls['handlingType']?.value)) {
      this.transportItemForm?.controls['handlingType'].setValue(this.handlingTypesOpt[0].value);
    }
  }
}
